<script setup lang="ts">
import { ref, computed } from 'vue'
import { useOpsStore } from '@/stores/ops'
import { useOrgAssignmentStore } from '@/stores/orgAssignment'
import { useIAPStore } from '@/stores/iap'
import { usePersonnelStore } from '@/stores/personnel'
import { useMedicalPlanStore } from '@/stores/medicalPlan'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import type { OrgAssignment, MedicalPlanData } from '@/types'

const props = defineProps<{ incidentId: string }>()
defineEmits<{ prev: []; next: [] }>()

const opsStore = useOpsStore()
const orgAssignmentStore = useOrgAssignmentStore()
const iapStore = useIAPStore()
const personnelStore = usePersonnelStore()
const medicalPlanStore = useMedicalPlanStore()

const opId = computed(() => opsStore.selectedOpId ?? '')

const org = computed(() => orgAssignmentStore.orgAssignment)

// Personnel name suggestions (checked in, not checked out)
const rosteredNames = computed(() =>
  personnelStore.roster
    .filter((p) => p.time_out === null)
    .map((p) => `${p.first} ${p.last}`),
)

async function save(updates: Partial<OrgAssignment>) {
  if (!opId.value) return
  await orgAssignmentStore.saveOrgAssignment(props.incidentId, opId.value, updates)
}

function getStr(field: keyof OrgAssignment): string {
  const v = org.value?.[field]
  return typeof v === 'string' ? v : ''
}

function getNamedRole(field: 'incident_commander' | 'search_manager') {
  return org.value?.[field] ?? { name: '', agency: '' }
}

// Agency reps dynamic array
const agencyReps = computed(() => org.value?.agency_reps ?? [])

async function addAgencyRep() {
  await save({ agency_reps: [...agencyReps.value, { name: '', agency: '' }] })
}

async function updateAgencyRep(index: number, key: 'name' | 'agency', value: string) {
  const updated = agencyReps.value.map((r, i) => i === index ? { ...r, [key]: value } : r)
  await save({ agency_reps: updated })
}

async function removeAgencyRep(index: number) {
  await save({ agency_reps: agencyReps.value.filter((_, i) => i !== index) })
}

// Divisions
const divisions = computed(() => org.value?.divisions ?? [])

async function addDivision() {
  await save({ divisions: [...divisions.value, { label: '', supervisor: '' }] })
}

async function updateDivision(index: number, key: 'label' | 'supervisor', value: string) {
  const updated = divisions.value.map((d, i) => i === index ? { ...d, [key]: value } : d)
  await save({ divisions: updated })
}

async function removeDivision(index: number) {
  await save({ divisions: divisions.value.filter((_, i) => i !== index) })
}

// Transportation (string array)
const transportation = computed(() => org.value?.transportation ?? [])
const newTransport = ref('')

async function addTransport() {
  const name = newTransport.value.trim()
  if (!name) return
  await save({ transportation: [...transportation.value, name] })
  newTransport.value = ''
}

async function removeTransport(index: number) {
  await save({ transportation: transportation.value.filter((_, i) => i !== index) })
}

// Medical unit leader — shared with IAP206 (both read/write medicalPlan.medical_unit_leader)
const medPlan = computed((): MedicalPlanData => medicalPlanStore.medicalPlan ?? {
  aid_stations: [],
  transportation: [],
  hospitals: [],
  emergency_procedures: '',
  k9_vet_info: '',
  medical_unit_leader: '',
})
async function saveMedicalUnitLeader(value: string) {
  if (!opId.value) return
  await medicalPlanStore.saveMedicalPlan(props.incidentId, opId.value, { ...medPlan.value, medical_unit_leader: value })
}

// Prepared by
const preparedBy203 = computed(() => iapStore.iap?.prepared_by_203 ?? '')
async function savePreparedBy(value: string) {
  if (!opId.value) return
  await iapStore.savePreparedBy(props.incidentId, opId.value, '203', value)
}

const datalistId = 'iap203-names'
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <datalist :id="datalistId">
      <option v-for="name in rosteredNames" :key="name" :value="name" />
    </datalist>

    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 203 · Required</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Organization Assignment</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Command structure, section chiefs, staff, agency reps, divisions, and support.</p>
    </div>

    <!-- Command group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Command</h3>
      </div>
      <div class="p-3.5 grid grid-cols-2 gap-3">
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Commander</label>
          <input :list="datalistId" :value="getNamedRole('incident_commander').name" class="w-full bg-surface text-on-surface font-body text-[13px] px-2.5 py-2 border border-border rounded focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)]" placeholder="Name…" @blur="save({ incident_commander: { ...getNamedRole('incident_commander'), name: ($event.target as HTMLInputElement).value } })" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Agency</label>
          <FosInput :model-value="getNamedRole('incident_commander').agency" placeholder="Agency…" @blur="save({ incident_commander: { ...getNamedRole('incident_commander'), agency: ($event.target as HTMLInputElement).value } })" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Search Manager</label>
          <input :list="datalistId" :value="getNamedRole('search_manager').name" class="w-full bg-surface text-on-surface font-body text-[13px] px-2.5 py-2 border border-border rounded focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)]" placeholder="Name…" @blur="save({ search_manager: { ...getNamedRole('search_manager'), name: ($event.target as HTMLInputElement).value } })" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Agency</label>
          <FosInput :model-value="getNamedRole('search_manager').agency" placeholder="Agency…" @blur="save({ search_manager: { ...getNamedRole('search_manager'), agency: ($event.target as HTMLInputElement).value } })" />
        </div>
      </div>
    </div>

    <!-- Section Chiefs group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Section Chiefs</h3>
      </div>
      <div class="p-3.5 grid grid-cols-2 gap-3">
        <div v-for="(cfg, i) in [
          { label: 'Plans Chief', field: 'plans_chief' },
          { label: 'Plans Deputy', field: 'plans_deputy' },
          { label: 'Operations Chief', field: 'ops_chief' },
          { label: 'Operations Deputy', field: 'ops_deputy' },
          { label: 'Logistics Chief', field: 'logistics_chief' },
          { label: 'Logistics Deputy', field: 'logistics_deputy' },
          { label: 'Intel Chief', field: 'intel_chief' },
        ]" :key="i">
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">{{ cfg.label }}</label>
          <input :list="datalistId" :value="getStr(cfg.field as keyof OrgAssignment)" class="w-full bg-surface text-on-surface font-body text-[13px] px-2.5 py-2 border border-border rounded focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)]" :placeholder="`${cfg.label}…`" @blur="save({ [cfg.field]: ($event.target as HTMLInputElement).value })" />
        </div>
      </div>
    </div>

    <!-- Staff group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Staff</h3>
      </div>
      <div class="p-3.5 grid grid-cols-2 gap-3">
        <div v-for="(cfg, i) in [
          { label: 'PIO', field: 'pio' },
          { label: 'Liaison', field: 'liaison' },
          { label: 'Safety Officer', field: 'safety_officer' },
        ]" :key="i">
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">{{ cfg.label }}</label>
          <input :list="datalistId" :value="getStr(cfg.field as keyof OrgAssignment)" class="w-full bg-surface text-on-surface font-body text-[13px] px-2.5 py-2 border border-border rounded focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(23,80,158,0.15)]" :placeholder="`${cfg.label}…`" @blur="save({ [cfg.field]: ($event.target as HTMLInputElement).value })" />
        </div>
      </div>
    </div>

    <!-- Agency Reps group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Agency Representatives</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-2">
        <div v-for="(rep, idx) in agencyReps" :key="idx" class="flex gap-2 items-center">
          <FosInput :model-value="rep.name" placeholder="Name…" @blur="updateAgencyRep(idx, 'name', ($event.target as HTMLInputElement).value)" />
          <FosInput :model-value="rep.agency" placeholder="Agency…" @blur="updateAgencyRep(idx, 'agency', ($event.target as HTMLInputElement).value)" />
          <button class="text-outline hover:text-error shrink-0" @click="removeAgencyRep(idx)">
            <span class="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
        <p v-if="agencyReps.length === 0" class="font-body text-[13px] text-outline">No agency representatives added.</p>
        <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-3 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary self-start transition-colors mt-1" @click="addAgencyRep">
          <span class="material-symbols-outlined text-[14px]">add</span>
          Add Agency Rep
        </button>
      </div>
    </div>

    <!-- Divisions group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Divisions</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-2">
        <div v-for="(div, idx) in divisions" :key="idx" class="flex gap-2 items-center">
          <FosInput :model-value="div.label" placeholder="Division label…" @blur="updateDivision(idx, 'label', ($event.target as HTMLInputElement).value)" />
          <FosInput :model-value="div.supervisor" placeholder="Supervisor name…" @blur="updateDivision(idx, 'supervisor', ($event.target as HTMLInputElement).value)" />
          <button class="text-outline hover:text-error shrink-0" @click="removeDivision(idx)">
            <span class="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
        <p v-if="divisions.length === 0" class="font-body text-[13px] text-outline">No divisions added.</p>
        <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-3 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary self-start transition-colors mt-1" @click="addDivision">
          <span class="material-symbols-outlined text-[14px]">add</span>
          Add Division
        </button>
      </div>
    </div>

    <!-- Support group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Support</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <div v-for="(cfg, i) in [
          { label: 'Communications', field: 'comms' },
          { label: 'Food / Water', field: 'food_water' },
        ]" :key="i">
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">{{ cfg.label }}</label>
          <FosInput :model-value="getStr(cfg.field as keyof OrgAssignment)" :placeholder="`${cfg.label}…`" @blur="save({ [cfg.field]: ($event.target as HTMLInputElement).value })" />
        </div>
        <div>
          <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Medical Unit Leader</label>
          <FosInput :model-value="medPlan.medical_unit_leader" placeholder="Medical Unit Leader…" @blur="saveMedicalUnitLeader(($event.target as HTMLInputElement).value)" />
        </div>
      </div>
    </div>

    <!-- Transportation group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Transportation</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <div class="flex flex-wrap gap-2">
          <span v-for="(driver, idx) in transportation" :key="idx" class="inline-flex items-center gap-1 bg-surface-container-low border border-border rounded px-2 py-1 font-body text-[13px]">
            {{ driver }}
            <button class="text-outline hover:text-error" @click="removeTransport(idx)">
              <span class="material-symbols-outlined text-[14px]">close</span>
            </button>
          </span>
        </div>
        <div class="flex gap-2">
          <FosInput v-model="newTransport" placeholder="Driver name…" @keyup.enter="addTransport" />
          <FosButton variant="secondary" size="sm" @click="addTransport">Add</FosButton>
        </div>
      </div>
    </div>

    <!-- Prepared By group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
      </div>
      <div class="p-3.5">
        <FosInput :model-value="preparedBy203" placeholder="Name of person preparing ICS 203…" @blur="savePreparedBy(($event.target as HTMLInputElement).value)" />
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors" @click="$emit('prev')">
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        ICS 202
      </button>
      <span class="flex-1" />
      <button class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity" @click="$emit('next')">
        ICS 205
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>
  </div>
</template>
